import {inject, NgModule} from '@angular/core';
import {Route, Router, RouterModule, Routes, UrlTree} from '@angular/router';
import {MaintenanceComponent} from 'src/app/maintenance/maintenance.component';
import {AuthRedirectComponent} from 'src/app/auth-redirect/auth-redirect.component';
import {MainComponent} from 'src/app/main/main.component';
import {IframeComponent} from '@shared/iframe/iframe.component';
import {WebbillLoginComponent} from 'src/app/webbill-login/webbill-login.component';
import {BuildingComponent} from 'src/app/building/building/building.component';
import {environment} from 'src/environments/environment';
import {LogOutComponent} from 'src/app/log-out/log-out.component';
import {WelcomeComponent} from 'src/app/welcome/welcome.component';
import {ApiTokenTableComponent} from './api-token-table/api-token-table.component';
import {ResidentAppComponent} from 'src/app/resident-app/resident-app.component';
import {SelectBuildingPageComponent} from 'src/app/select-building/select-building-page/select-building-page.component';
import {ReactOnlineIframeComponent} from 'src/app/building/react-online-iframe/react-online-iframe.component';
import {TerminationOfContractComponent} from 'src/app/user-menu/termination-of-contract/termination-of-contract.component';
import {
    buildingResolver,
    consumerResolver,
    loadedUserState,
    locationResolver,
    userResolver,
} from '@shared/route-utils';
import {unsavedChangesGuard} from '@brunata-angular/ui';
import {administratorGuard} from './user-administration/administrator.guard';
import {meterRoutes} from 'src/app/meter/routes';
import {monitoringRoutes} from 'src/app/monitoring/routes';
import {SubMenuComponent} from 'src/app/building/sub-menu/sub-menu.component';
import {BuildingServices, FeatureFlagService} from '@brunata-angular/util';
import {MyProfileComponent} from 'src/app/my-profile/my-profile.component';
import {ConsumptionsReportsComponent} from 'src/app/consumption-reports/consumptions-reports.component';
import {NewUserComponent} from 'src/app/new-user/new-user.component';
import {ResetPasswordComponent} from 'src/app/reset-password/reset-password.component';
import {NotFoundComponent} from 'src/app/not-found/not-found.component';
import {DocViewerComponent} from 'src/app/doc-viewer/doc-viewer.component';
import {ExportComponent} from 'src/app/export/export.component';
import {ACROSS_SYSTEMS_EXPORT_SERVICES, exportRoutes} from 'src/app/export/routes';
import {InstallationProjectsComponent} from 'src/app/installation-projects/installation-projects.component';
import {InstallationProjectDetailComponent} from 'src/app/installation-projects/detail/installation-project-detail.component';
import {InstallationProjectLocationsComponent} from 'src/app/installation-projects/detail/installation-project-locations.component';
import {InstallationProjectOverviewComponent} from 'src/app/installation-projects/detail/installation-project-overview.component';
import {INSTALLATION_PROJECTS_FEATURE_FLAG} from 'src/app/installation-projects/installation-projects.model';
import {ResidentsComponent} from './residents/residents.component';
import {consumerProfileRoute} from './residents/consumer-profile/routes';
import {Observable, map} from 'rxjs';
import {UserAdministrationComponent} from './user-administration/user-administration.component';
import {UserDetailComponent} from './user-administration/user-detail/user-detail.component';
import {CreateUserComponent} from './user-administration/create-user/create-user.component';

export const archiveRoutes: Route[] = [
    {path: '', redirectTo: 'accountingdocs', pathMatch: 'full'},
    {
        path: 'accountingdocs',
        component: ReactOnlineIframeComponent,
        data: {
            key: 'titles_accountingDocuments',
            module: 'webArchive',
            reactRoute: 'buildings/{buildingno}/webarchive/accountingdocs',
        },
        children: [
            {
                path: 'allocation-lists',
                component: ReactOnlineIframeComponent,
                data: {
                    key: 'titles_allocation-lists',
                    module: 'webArchive',
                    reactRoute: 'buildings/{buildingno}/webarchive/accountingdocs/allocation-lists',
                },
            },
            {
                path: 'bills',
                component: ReactOnlineIframeComponent,
                data: {
                    key: 'titles_bills',
                    module: 'webArchive',
                    reactRoute: 'buildings/{buildingno}/webarchive/accountingdocs/bills',
                },
            },
            {
                path: 'billing-results',
                component: ReactOnlineIframeComponent,
                data: {
                    key: 'titles_billing-results',
                    module: 'webArchive',
                    reactRoute: 'buildings/{buildingno}/webarchive/accountingdocs/billing-results',
                },
            },
            {
                path: 'bill-files',
                component: ReactOnlineIframeComponent,
                data: {
                    key: 'titles_bill-files',
                    module: 'webArchive',
                    reactRoute: 'buildings/{buildingno}/webarchive/accountingdocs/bill-files',
                },
            },
            {
                path: 'moveout-bills',
                component: ReactOnlineIframeComponent,
                data: {
                    key: 'titles_moveout-bills',
                    module: 'webArchive',
                    reactRoute: 'buildings/{buildingno}/webarchive/accountingdocs/moveout-bills',
                },
            },
            {
                path: 'adjustment-amounts',
                component: ReactOnlineIframeComponent,
                data: {
                    key: 'titles_adjustment-amounts',
                    module: 'webArchive',
                    reactRoute:
                        'buildings/{buildingno}/webarchive/accountingdocs/adjustment-amounts',
                },
            },
            {
                path: 'bill-packages',
                component: ReactOnlineIframeComponent,
                data: {
                    key: 'titles_bill-packages',
                    module: 'webArchive',
                    reactRoute: 'buildings/{buildingno}/webarchive/accountingdocs/bill-packages',
                },
            },
        ],
    },
    {
        path: 'releasebills',
        component: ReactOnlineIframeComponent,
        data: {
            key: 'titles_releaseBills',
            module: 'systems',
            reactRoute: 'buildings/{buildingno}/webarchive/releases',
        },
    },
    {
        path: 'receipts',
        component: ReactOnlineIframeComponent,
        data: {
            key: 'titles_receipts',
            module: 'webArchive',
            reactRoute: 'buildings/{buildingno}/webarchive/receipts/service-work',
        },
    },
];

export const routes: Routes = [
    {
        // Administrator portal
        path: 'admin',
        component: MainComponent,
        children: [
            {path: '', redirectTo: 'system', pathMatch: 'full'},
            {
                path: 'system',
                component: SelectBuildingPageComponent,
                data: {module: 'systems', key: 'titles_systems'},
                children: [
                    {
                        path: ':buildingno',
                        component: BuildingComponent,
                        resolve: {breadcrumb: buildingResolver},
                        children: [
                            {path: '', redirectTo: 'information', pathMatch: 'full'},
                            {
                                path: 'information',
                                component: ReactOnlineIframeComponent,
                                data: {
                                    module: 'systems',
                                    key: 'titles_information',
                                    reactRoute: 'buildings/{buildingno}/detail',
                                },
                            },
                            // new residents page - matches when a feature flag is enabled
                            {
                                path: 'consumers',
                                canMatch: [
                                    (): Observable<boolean> => {
                                        const featureFlagService = inject(FeatureFlagService);
                                        return featureFlagService.isFeatureFlagEnabled$(
                                            'kehe-online-project',
                                        );
                                    },
                                ],
                                data: {
                                    module: 'systems',
                                    key: 'titles_residents',
                                    featureFlag: 'kehe-online-project',
                                },
                                children: [
                                    {
                                        path: '',
                                        pathMatch: 'full',
                                        component: ResidentsComponent,
                                    },
                                    consumerProfileRoute(
                                        'location/:locationid/resident/:consumerid',
                                    ),
                                    consumerProfileRoute(':locationid/resident/:consumerid'),
                                    {
                                        path: 'location',
                                        component: ReactOnlineIframeComponent,
                                        data: {
                                            module: 'systems',
                                            key: 'titles_apartments',
                                            reactRoute: 'buildings/{buildingno}/detail/apartments',
                                        },
                                        children: [
                                            {
                                                path: ':locationid',
                                                data: {
                                                    reactRoute:
                                                        'buildings/{buildingno}/detail/apartments/{locationid}',
                                                },
                                                resolve: {breadcrumb: locationResolver},
                                                component: ReactOnlineIframeComponent,
                                                children: [
                                                    {
                                                        path: 'resident/:consumerid',
                                                        resolve: {breadcrumb: consumerResolver},
                                                        component: ReactOnlineIframeComponent,
                                                        data: {
                                                            reactRoute:
                                                                'buildings/{buildingno}/residents/all-residents/{consumerid}?locationId={locationid}',
                                                        },
                                                        children: [
                                                            {
                                                                path: 'register-move-out',
                                                                component:
                                                                    ReactOnlineIframeComponent,
                                                                data: {
                                                                    module: 'residents',
                                                                    key: 'actions_registerMoveOut',
                                                                    reactRoute:
                                                                        'buildings/{buildingno}/residents/all-residents/{consumerid}/register-move-out?locationId={locationid}',
                                                                },
                                                            },
                                                        ],
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: '',
                                        component: ReactOnlineIframeComponent,
                                        children: [
                                            {
                                                path: ':locationid/resident/:consumerid',
                                                resolve: {breadcrumb: consumerResolver},
                                                component: ReactOnlineIframeComponent,
                                                data: {
                                                    reactRoute:
                                                        'buildings/{buildingno}/residents/all-residents/{consumerid}?locationId={locationid}',
                                                },
                                                children: [
                                                    {
                                                        path: 'register-move-out',
                                                        component: ReactOnlineIframeComponent,
                                                        data: {
                                                            module: 'residents',
                                                            key: 'actions_registerMoveOut',
                                                            reactRoute:
                                                                'buildings/{buildingno}/residents/all-residents/{consumerid}/register-move-out?locationId={locationid}',
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            consumerProfileRoute('residents/:locationid/resident/:consumerid'),
                            // old residents page - fallback when a feature flag is disabled
                            {
                                path: 'residents',
                                canMatch: [
                                    (): Observable<boolean> =>
                                        inject(FeatureFlagService).isFeatureFlagEnabled$({
                                            id: 'kehe-online-project',
                                            hideWhenEnabled: true,
                                        }),
                                ],
                                component: ReactOnlineIframeComponent,
                                data: {
                                    module: 'systems',
                                    key: 'titles_residents',
                                    reactRoute: 'buildings/{buildingno}/residents',
                                    featureFlag: {
                                        id: 'kehe-online-project',
                                        hideWhenEnabled: true,
                                    },
                                },
                                children: [
                                    {
                                        path: ':locationid/resident/:consumerid',
                                        resolve: {breadcrumb: consumerResolver},
                                        component: ReactOnlineIframeComponent,
                                        data: {
                                            reactRoute:
                                                'buildings/{buildingno}/residents/all-residents/{consumerid}?locationId={locationid}',
                                        },
                                        children: [
                                            {
                                                path: 'register-move-out',
                                                component: ReactOnlineIframeComponent,
                                                data: {
                                                    module: 'residents',
                                                    key: 'actions_registerMoveOut',
                                                    reactRoute:
                                                        'buildings/{buildingno}/residents/all-residents/{consumerid}/register-move-out?locationId={locationid}',
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            consumerProfileRoute('location/:locationid/resident/:consumerid'),
                            {
                                path: 'location',
                                canMatch: [
                                    (): Observable<boolean> =>
                                        inject(FeatureFlagService).isFeatureFlagEnabled$({
                                            id: 'kehe-online-project',
                                            hideWhenEnabled: true,
                                        }),
                                ],
                                component: ReactOnlineIframeComponent,
                                data: {
                                    module: 'systems',
                                    key: 'titles_apartments',
                                    reactRoute: 'buildings/{buildingno}/detail/apartments',
                                    featureFlag: {
                                        id: 'kehe-online-project',
                                        hideWhenEnabled: true,
                                    },
                                },
                                children: [
                                    {
                                        path: ':locationid',
                                        data: {
                                            reactRoute:
                                                'buildings/{buildingno}/detail/apartments/{locationid}',
                                        },
                                        resolve: {breadcrumb: locationResolver},
                                        component: ReactOnlineIframeComponent,
                                        children: [
                                            {
                                                path: 'resident/:consumerid',
                                                resolve: {breadcrumb: consumerResolver},
                                                component: ReactOnlineIframeComponent,
                                                data: {
                                                    reactRoute:
                                                        'buildings/{buildingno}/residents/all-residents/{consumerid}?locationId={locationid}',
                                                },
                                                children: [
                                                    {
                                                        path: 'register-move-out',
                                                        component: ReactOnlineIframeComponent,
                                                        data: {
                                                            module: 'residents',
                                                            key: 'actions_registerMoveOut',
                                                            reactRoute:
                                                                'buildings/{buildingno}/residents/all-residents/{consumerid}/register-move-out?locationId={locationid}',
                                                        },
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                ],
                            },
                            // MyBrunata's "back to admin portal" button builds this older path, so
                            // this redirect has to outlive the 'location' route above that shadows it.
                            {
                                path: 'location/:locationid/resident/:consumerid',
                                pathMatch: 'full',
                                redirectTo: 'consumers/:locationid/resident/:consumerid',
                                data: {hide: true},
                            },
                            {
                                path: 'meters',
                                component: SubMenuComponent,
                                data: {key: 'utilities_meter_and_sensors', module: 'ngonline'},
                                children: meterRoutes,
                            },
                            {
                                path: 'monitoring',
                                component: SubMenuComponent,
                                data: {
                                    module: 'common',
                                    key: 'titles_monitoringAlarms',
                                    requireBuildingService:
                                        BuildingServices.EVENTS_AGENTS_AND_ALARMS,
                                },
                                children: monitoringRoutes,
                            },
                            {
                                path: 'reports',
                                component: ReactOnlineIframeComponent,
                                data: {
                                    module: 'systems',
                                    key: 'titles_reports',
                                    reactRoute: 'buildings/{buildingno}/detail/reports',
                                    requireBuildingService: BuildingServices.REPORT_AGENT,
                                },
                                children: [
                                    {
                                        path: 'report-consumption-r',
                                        component: ReactOnlineIframeComponent,
                                        data: {
                                            module: 'systems',
                                            key: 'titles_consumptionReport',
                                            reactRoute:
                                                'buildings/{buildingno}/detail/reports/report-consumption-r',
                                        },
                                    },
                                    {
                                        path: 'meter-values-form',
                                        component: ReactOnlineIframeComponent,
                                        data: {
                                            module: 'systems',
                                            key: 'titles_meterReadings',
                                            reactRoute:
                                                'buildings/{buildingno}/detail/reports/meter-values-form',
                                        },
                                    },
                                    {
                                        path: 'deltatform',
                                        component: ReactOnlineIframeComponent,
                                        data: {
                                            module: 'systems',
                                            key: 'titles_deltaT-form',
                                            reactRoute:
                                                'buildings/{buildingno}/detail/reports/deltatform',
                                        },
                                    },
                                    {
                                        path: 'report-semimonthly-data',
                                        component: ReactOnlineIframeComponent,
                                        data: {
                                            module: 'systems',
                                            key: 'titles_report-semimonthly-data',
                                            reactRoute:
                                                'buildings/{buildingno}/detail/reports/report-semimonthly-data',
                                        },
                                    },
                                    {
                                        path: 'report-readings-for-export',
                                        component: ReactOnlineIframeComponent,
                                        data: {
                                            module: 'systems',
                                            key: 'titles_report-readings-for-export',
                                            reactRoute:
                                                'buildings/{buildingno}/detail/reports/report-readings-for-export',
                                        },
                                    },
                                ],
                            },
                            {
                                path: 'eed',
                                component: ReactOnlineIframeComponent,
                                data: {
                                    module: 'systems',
                                    key: 'titles_notifications-EED',
                                    reactRoute: 'buildings/{buildingno}/detail/eednotifications',
                                    requireBuildingService:
                                        BuildingServices.EED_EMAIL_NOTIFICATIONS,
                                },
                            },
                            {
                                path: 'archive',
                                component: SubMenuComponent,
                                data: {key: 'titles_archive', module: 'common'},
                                children: archiveRoutes,
                            },
                        ],
                    },
                ],
            },
            {
                path: 'reporting',
                data: {key: 'titles_reporting', module: 'common'},
                loadChildren: (): Promise<{default: Route[]}> => import('src/app/reporting/routes'),
            },
            {
                path: 'visual',
                component: IframeComponent,
                data: {url: environment.visualUrl, skipBreadcrumb: true},
            },
            {
                path: 'documentation',
                component: IframeComponent,
                data: {url: environment.controlManualUrl, skipBreadcrumb: true},
            },
            {
                path: 'export',
                canActivate: [
                    (): Observable<boolean | UrlTree> => {
                        const router = inject(Router);
                        return loadedUserState().pipe(
                            map(state =>
                                state.status === 'success' &&
                                ACROSS_SYSTEMS_EXPORT_SERVICES.some(service =>
                                    state.user.buildingServices.includes(service),
                                )
                                    ? true
                                    : router.parseUrl('/admin'),
                            ),
                        );
                    },
                ],
                data: {key: 'titles_export', module: 'common', redirectsToChild: true},
                component: ExportComponent,
                children: exportRoutes,
            },
            // Brunata installation projects read from Dynamics 365 (AGI-19637)
            {
                path: 'installation-projects',
                // The list lives on the parent node (the select-building-page shape), so the
                // page shows a single crumb and the detail keeps this node as its ancestor link.
                component: InstallationProjectsComponent,
                canMatch: [
                    (): Observable<boolean> =>
                        inject(FeatureFlagService).isFeatureEnabled$(
                            INSTALLATION_PROJECTS_FEATURE_FLAG,
                        ),
                ],
                data: {
                    key: 'titles_installationProjects',
                    module: 'common',
                    featureFlag: INSTALLATION_PROJECTS_FEATURE_FLAG,
                },
                children: [
                    {
                        path: ':projectId',
                        component: InstallationProjectDetailComponent,
                        data: {
                            key: 'installationProjects_detail_crumb',
                            module: 'ngonline',
                        },
                        children: [
                            {path: '', redirectTo: 'overview', pathMatch: 'full'},
                            {
                                path: 'overview',
                                component: InstallationProjectOverviewComponent,
                                data: {
                                    key: 'installationProjects_detail_tab_overview',
                                    module: 'ngonline',
                                },
                            },
                            {
                                path: 'locations',
                                component: InstallationProjectLocationsComponent,
                                data: {
                                    key: 'installationProjects_detail_tab_locations',
                                    module: 'ngonline',
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'user-administration',
                data: {
                    key: 'titles_userAdministration',
                    module: 'userAdministration',
                },
                children: [
                    {
                        path: '',
                        component: UserAdministrationComponent,
                        data: {skipBreadcrumb: true},
                    },
                    {
                        path: 'create-user',
                        component: CreateUserComponent,
                        canActivate: [administratorGuard],
                        canDeactivate: [unsavedChangesGuard],
                        data: {key: 'titles_createUser', module: 'userAdministration'},
                    },
                    {
                        path: ':loginNameId',
                        component: UserDetailComponent,
                        canDeactivate: [unsavedChangesGuard],
                        resolve: {breadcrumb: userResolver},
                    },
                ],
            },
            {
                path: 'my-profile',
                component: MyProfileComponent,
                data: {skipBreadcrumb: true},
            },
            {
                path: 'termination-of-contract',
                component: TerminationOfContractComponent,
                data: {key: 'actions_cancelSystem', module: 'common', skipBreadcrumb: true},
            },
            {path: 'api-tokens', component: ApiTokenTableComponent, data: {skipBreadcrumb: true}},
        ],
    },
    {
        // Resident portal
        path: 'resident',
        component: ResidentAppComponent,
    },
    {
        path: 'webarchive',
        loadChildren: (): Promise<{default: Route[]}> => import('src/app/webarchive/routes'),
    },
    {path: 'consumption-reports/building/:buildingno', component: ConsumptionsReportsComponent},
    {path: 'maintenance', component: MaintenanceComponent},
    {path: 'auth-redirect', component: AuthRedirectComponent},
    {path: 'webbill-login', component: WebbillLoginComponent},
    {path: 'log-out', component: LogOutComponent},
    {path: 'new-user/resident', component: NewUserComponent},
    {path: 'reset-password', component: ResetPasswordComponent},
    {path: 'auth-response', redirectTo: ''},
    {path: '', component: WelcomeComponent},
    {path: 'eed-login', component: DocViewerComponent},
    {path: '**', component: NotFoundComponent},
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppRoutingModule {}
